import React, {useState, useEffect, FC} from 'react'
import axios from 'axios'
import {PageTitle} from '../../../../_designereyes/layout/core'
import {API_URL} from '../../../modules/auth/core/_requests'

// PrimeReact components
import {DataTable, DataTableFilterMeta} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {Chart} from 'primereact/chart'

// If not importing from a global file, uncomment these:
// import 'primereact/resources/themes/arya-blue/theme.css'
// import 'primereact/resources/primereact.min.css'
import './BuyBoxDashboardPage.css'

interface BuyBoxDataRaw {
  marketplace: string
  sku: string
  asin: string
  title: string
  buy_box_winner: string
  buy_box_price: number
  my_price: number
  cost: number
  fba_fee: number
  quantity: number
}

interface BuyBoxData extends BuyBoxDataRaw {
  costTotal: number
  marginAbs: number
  marginPct: number
  priceDiff: number
  priceDiffPct: number
}

export const BuyBoxDashboardPage: FC = () => {
  const [data, setData] = useState<BuyBoxData[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * menu-based filters
   * By default: quantity > 0 (hide zero-inventory)
   */
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    asin: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    title: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    buy_box_winner: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    buy_box_price: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    my_price: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    costTotal: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    marginAbs: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    priceDiff: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    quantity: {operator: 'and', constraints: [{value: 0, matchMode: 'gt'}]},
    global: {value: '', matchMode: 'contains'},
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const resp = await axios.get(`${API_URL}/api/buybox`)
      const raw: BuyBoxDataRaw[] = resp.data

      // Build additional numeric fields used for sorting/filter
      const mapped = raw.map((row) => {
        // costTotal = cost + fba_fee if cost>0
        const costTotal = row.cost > 0 ? row.cost + row.fba_fee : 0

        // marginAbs = my_price - costTotal, marginPct = (marginAbs / my_price)*100
        let marginAbs = 0
        let marginPct = 0
        if (row.cost > 0 && row.my_price > 0) {
          marginAbs = row.my_price - costTotal
          marginPct = (marginAbs / row.my_price) * 100
        }

        // priceDiff = my_price - buy_box_price
        let priceDiff = row.my_price - row.buy_box_price
        let priceDiffPct = 0
        if (row.buy_box_price > 0) {
          priceDiffPct = (priceDiff / row.buy_box_price) * 100
        }

        return {
          ...row,
          costTotal,
          marginAbs,
          marginPct,
          priceDiff,
          priceDiffPct,
        }
      })

      setData(mapped)
    } catch (err) {
      console.error('Error fetching buybox data:', err)
    } finally {
      setLoading(false)
    }
  }

  // ========== ANALYTICS ==========
  // Only marketplace='AUS', quantity>0, buy_box_price>0
  const analyticsData = data.filter(
    (d) => d.marketplace === 'AUS' && d.quantity > 0 && d.buy_box_price > 0
  )

  // 1) Buy Box Ownership
  const winCount = analyticsData.filter((d) => d.buy_box_winner === 'EC AMZ').length
  const loseCount = analyticsData.length - winCount
  const buyBoxPieData = {
    labels: ['Win BB', 'Lose BB'],
    datasets: [
      {
        data: [winCount, loseCount],
        backgroundColor: ['#22c55e', '#ef4444'], // green and red
      },
    ],
  }

  // 2) Price-Matched but Not Winning => my_price===buy_box_price && buy_box_winner!=='EC AMZ'
  const priceMatchedNotWinningCount = analyticsData.filter(
    (d) => d.my_price === d.buy_box_price && d.buy_box_winner !== 'EC AMZ'
  ).length

  // ========== TABLE ROW & COLUMN RENDERS ==========
  // highlight row if not owned
  const rowClassName = (rowData: BuyBoxData) => {
    return rowData.buy_box_winner === 'EC AMZ' ? '' : 'not-owning-buybox'
  }

  // ASIN clickable
  const asinBodyTemplate = (rowData: BuyBoxData) => {
    if (!rowData.asin) return ''
    const link = `https://www.amazon.com/dp/${rowData.asin}`
    return (
      <a href={link} target='_blank' rel='noopener noreferrer'>
        {rowData.asin}
      </a>
    )
  }

  // Price Diff => "x.xx(y.yy%)" or "-" if 0
  const priceDiffBodyTemplate = (rowData: BuyBoxData) => {
    if (rowData.priceDiff === 0) return '-'
    const diffFixed = rowData.priceDiff.toFixed(2)
    const pctFixed = rowData.priceDiffPct.toFixed(2)
    return `${diffFixed}(${pctFixed}%)`
  }

  // Cost => "x.xx(y.yy)" or "-" if cost=0
  const costBodyTemplate = (rowData: BuyBoxData) => {
    if (rowData.cost === 0) return '-'
    const total = rowData.costTotal.toFixed(2)
    const fee = rowData.fba_fee.toFixed(2)
    return `${total}(${fee})`
  }

  // Margin => "absMargin(absPct%)" or "-" if cost=0 or marginAbs=0
  const marginBodyTemplate = (rowData: BuyBoxData) => {
    if (rowData.cost === 0 || rowData.marginAbs === 0) return '-'
    const absStr = rowData.marginAbs.toFixed(2)
    const pctStr = rowData.marginPct.toFixed(2)
    return `${absStr}(${pctStr}%)`
  }

  // BB Owned => "Yes"/"No"
  const bbOwnedBodyTemplate = (rowData: BuyBoxData) => {
    return rowData.buy_box_winner === 'EC AMZ' ? 'Yes' : 'No'
  }

  const onFilterChange = (e: any) => {
    setFilters(e.filters)
  }

  return (
    <>
      <PageTitle>Buy Box Dashboard</PageTitle>

      {/* 3 Cards in a row using Bootstrap */}
      <div className='row mb-3'>
        {/* 1) Buy Box Ownership */}
        <div className='col-12 col-md-4 mb-3 mb-md-0'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-3'>Buy Box Ownership</h5>
            <Chart type='pie' data={buyBoxPieData} style={{width: '12rem'}} />
            <p className='mt-2 mb-0 text-center'>
              {winCount} / {analyticsData.length} <span style={{opacity: 0.8}}>won</span>
            </p>
          </div>
        </div>

        {/* 2) Price-Matched but Not Winning */}
        <div className='col-12 col-md-4 mb-3 mb-md-0'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-2'>Price-Matched but Not Winning</h5>
            <span className='display-6'>{priceMatchedNotWinningCount}</span>
          </div>
        </div>

        {/* 3) Teaser Card */}
        <div className='col-12 col-md-4'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-2'>Want More Analytics?</h5>
            <p className='mb-0 text-center'>Choose what you want to see here</p>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className='card p-3 text-light buybox-dashboard'>
        <div className='mb-3'>
          <h2 className='mb-0'>Buy Box Dashboard</h2>
          <p className='text-muted'>
            Zero-inventory items are hidden by default. If cost=0, cost/margin are displayed as '-'.
          </p>
        </div>

        <DataTable
          value={data}
          paginator
          rows={200}
          rowsPerPageOptions={[50, 100, 200, 500]}
          loading={loading}
          filters={filters}
          filterDisplay='menu'
          onFilter={onFilterChange}
          sortMode='single'
          scrollable
          scrollHeight='600px'
          className='p-datatable-sm'
          rowClassName={rowClassName}
          dataKey='sku'
        >
          <Column
            field='asin'
            header='ASIN'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            body={asinBodyTemplate}
            style={{minWidth: '120px'}}
          />
          <Column
            field='title'
            header='Title'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            style={{minWidth: '200px'}}
          />
          <Column
            field='buy_box_winner'
            header='Buy Box Winner'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='buy_box_price'
            header='Buy Box Price'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='my_price'
            header='My Price'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='priceDiff'
            header='Price Diff'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
            body={priceDiffBodyTemplate}
          />
          <Column
            field='costTotal'
            header='Cost'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '120px'}}
            body={costBodyTemplate}
          />
          <Column
            field='marginAbs'
            header='Margin'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '120px'}}
            body={marginBodyTemplate}
          />
          <Column
            field='quantity'
            header='Quantity'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '100px'}}
          />
          <Column
            header='BB Owned'
            dataType='text'
            sortable
            style={{minWidth: '100px'}}
            body={bbOwnedBodyTemplate}
          />
        </DataTable>
      </div>
    </>
  )
}

export const BuyBoxDashboardWrapper: FC = () => {
  return <BuyBoxDashboardPage />
}
